import { useEffect, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { leadEditPatch, leadFormOf, leadStatusLabel, leadTypeLabel, LEAD_STATUSES, LEAD_TYPES, type LeadForm, type LeadPatch, type LeadRow } from '../../../lib/crm/leadSheet';
import { BTN_GHOST, BTN_PRIMARY } from '../../bureau/buttons';
import { FIELD } from '../../bureau/encre';
import { SheetBlock } from './EncreSheet';
import { SheetFormFooter } from './SheetFormFooter';

const LABEL = 'mb-1.5 block text-xs font-medium text-desk-soft';

function Field({ label, children, wide = false }: { label: string; children: ReactNode; wide?: boolean }) {
  return (
    <label className={`block min-w-0 ${wide ? 'sm:col-span-2' : ''}`}>
      <span className={LABEL}>{label}</span>
      {children}
    </label>
  );
}

/**
 * « Modifier » a lead in its sheet (PLAN-ENCRE-OUTILS O3 bis): the company, its contact, its
 * onboarding's type and status, its ERP, the team's notes. « Enregistrer » writes only what changed
 * and stays open on a refusal; the stage moves from the « Onboarding » block, through the back.
 */
export function LeadSheetEdit({
  lead,
  saving,
  error,
  onSave,
  onDone,
  onDirty,
}: {
  lead: LeadRow;
  saving: boolean;
  /** The last refusal, said above the buttons. */
  error: string | null;
  onSave: (patch: LeadPatch) => Promise<boolean>;
  onDone: () => void;
  onDirty: (dirty: boolean) => void;
}) {
  const [initial] = useState<LeadForm>(() => leadFormOf(lead));
  const [form, setForm] = useState<LeadForm>(initial);
  const [invalid, setInvalid] = useState<string | null>(null);
  const changed = (Object.keys(initial) as (keyof LeadForm)[]).some((k) => form[k] !== initial[k]);
  useEffect(() => onDirty(changed), [changed, onDirty]);
  useEffect(() => () => onDirty(false), [onDirty]);
  const text = (key: keyof LeadForm, label: string, opts: { wide?: boolean; type?: string } = {}) => (
    <Field label={label} wide={opts.wide}>
      <input type={opts.type ?? 'text'} className={FIELD} value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
    </Field>
  );
  // The statuses one may pick, and the lead's own when the back set another (`completed`).
  const statuses = LEAD_STATUSES.includes(initial.status) || !initial.status ? LEAD_STATUSES : [...LEAD_STATUSES, initial.status];

  const save = async () => {
    const result = leadEditPatch(lead, form);
    if ('error' in result) return setInvalid(result.error);
    setInvalid(null);
    if (Object.keys(result.patch).length === 0) return onDone();
    if (await onSave(result.patch)) onDone();
  };

  return (
    <div>
      <SheetBlock title="Entreprise">
        <div className="grid gap-3 sm:grid-cols-2">
          {text('company_name', 'Nom', { wide: true })}
          {text('siret', 'SIRET')}
          {text('erp', 'ERP')}
          {form.erp === 'other' && text('erp_other', 'ERP (autre)')}
          {text('address', 'Adresse', { wide: true })}
          {text('postal_code', 'Code postal')}
          {text('city', 'Ville')}
          {text('country', 'Pays')}
        </div>
      </SheetBlock>
      <SheetBlock title="Contact">
        <div className="grid gap-3 sm:grid-cols-2">
          {text('contact_first_name', 'Prénom')}
          {text('contact_last_name', 'Nom')}
          {text('contact_email', 'Mail', { type: 'email' })}
          {text('contact_phone', 'Téléphone', { type: 'tel' })}
        </div>
      </SheetBlock>
      <SheetBlock title="Onboarding">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Statut">
            <select className={FIELD} value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
              {statuses.map((s) => (
                <option key={s} value={s}>
                  {leadStatusLabel(s)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Type">
            <select className={FIELD} value={form.onboarding_type} onChange={(e) => setForm((f) => ({ ...f, onboarding_type: e.target.value }))}>
              <option value="">Non précisé</option>
              {(LEAD_TYPES.includes(initial.onboarding_type) || !initial.onboarding_type ? LEAD_TYPES : [...LEAD_TYPES, initial.onboarding_type]).map((t) => (
                <option key={t} value={t}>
                  {leadTypeLabel(t)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Notes internes" wide>
            <textarea
              rows={3}
              className={twMerge(FIELD, 'h-auto resize-none py-2')}
              value={form.internal_notes}
              onChange={(e) => setForm((f) => ({ ...f, internal_notes: e.target.value }))}
            />
          </Field>
        </div>
      </SheetBlock>
      <SheetFormFooter error={invalid ?? error}>
        <button type="button" onClick={onDone} disabled={saving} className={BTN_GHOST}>
          Annuler
        </button>
        <button type="button" onClick={() => void save()} disabled={saving} className={BTN_PRIMARY}>
          {saving && <Loader2 size={15} className="animate-spin" aria-hidden="true" />}
          Enregistrer
        </button>
      </SheetFormFooter>
    </div>
  );
}
